export const OPENAI_MODELS = {
  GPT_4: 'gpt-4',
  GPT_4_TURBO: 'gpt-4-turbo-preview',
  GPT_3_5_TURBO: 'gpt-3.5-turbo',
  GPT_4O_MINI: 'gpt-4o-mini',
  GPT_4_1_MINI: 'gpt-4.1-mini',
  O3_MINI: 'o3-mini'
} as const;

// Strategic model routing - GPT-4.1 Mini as primary, o3-mini for reasoning
export const DEFAULT_MODEL = OPENAI_MODELS.GPT_4_1_MINI;
export const BUDGET_MODEL = OPENAI_MODELS.GPT_4O_MINI;
export const REASONING_MODEL = OPENAI_MODELS.O3_MINI;

export const MODEL_CONFIGS = {
  [OPENAI_MODELS.GPT_4]: {
    maxTokens: 4000,
    temperature: 0.7,
    presencePenalty: 0.1,
    frequencyPenalty: 0.1,
    cost: { input: 30.0, output: 60.0 }, // per million tokens
    contextWindow: 8192
  },
  [OPENAI_MODELS.GPT_4_TURBO]: {
    maxTokens: 4000,
    temperature: 0.7,
    presencePenalty: 0.1,
    frequencyPenalty: 0.1,
    cost: { input: 10.0, output: 30.0 },
    contextWindow: 128000
  },
  [OPENAI_MODELS.GPT_3_5_TURBO]: {
    maxTokens: 2000,
    temperature: 0.7,
    presencePenalty: 0,
    frequencyPenalty: 0,
    cost: { input: 0.5, output: 1.5 },
    contextWindow: 16384
  },
  [OPENAI_MODELS.GPT_4O_MINI]: {
    maxTokens: 6000, // Increased for comprehensive analysis
    temperature: 0.5, // Lower for faster, more focused responses
    presencePenalty: 0.05,
    frequencyPenalty: 0.05,
    cost: { input: 0.15, output: 0.60 }, // Cheapest option
    contextWindow: 128000
  },
  [OPENAI_MODELS.GPT_4_1_MINI]: {
    maxTokens: 8000,
    temperature: 0.7,
    presencePenalty: 0.1,
    frequencyPenalty: 0.1,
    cost: { input: 0.40, output: 1.60 }, // Best value primary model
    contextWindow: 1000000 // 1M tokens
  },
  [OPENAI_MODELS.O3_MINI]: {
    maxTokens: 4000,
    temperature: 0.7,
    presencePenalty: 0.1,
    frequencyPenalty: 0.1,
    cost: { input: 1.10, output: 4.40 }, // Reasoning model
    contextWindow: 200000,
    useMaxCompletionTokens: true, // o3 models use max_completion_tokens instead of max_tokens
    disableTemperature: true, // o3 models don't support temperature
    disablePenalties: true // o3 models don't support presence/frequency penalties
  }
} as const;

// Task complexity types for intelligent model routing
export type TaskComplexity = 
  | 'simple'           // Basic text generation, simple formatting
  | 'standard'         // Job descriptions, candidate analysis, general AI features
  | 'complex'          // Bias detection, scoring algorithms, technical analysis
  | 'reasoning';       // Mathematical analysis, complex logic, deep reasoning

// Task type definitions for model routing
export type TaskType =
  // Simple tasks - use budget model (GPT-4o Mini)
  | 'text-generation'
  | 'basic-formatting'
  | 'simple-qa'
  
  // Standard tasks - use budget model for speed & cost (GPT-4o Mini)  
  | 'job-description'
  | 'candidate-analysis'
  | 'interview-questions'
  | 'market-intelligence'
  | 'auto-fill'
  | 'audience-targeting'
  
  // Complex reasoning tasks - use reasoning model (o3-mini)
  | 'bias-detection'
  | 'complex-scoring'
  | 'technical-analysis'
  | 'skills-assessment'
  | 'mathematical-analysis'
  | 'strategic-analysis';

/**
 * Intelligent model selection based on task type and complexity
 * Optimizes cost vs performance automatically
 */
export function getOptimalModel(taskType: TaskType): keyof typeof MODEL_CONFIGS {
  // Simple tasks: Use budget model for cost efficiency
  const simpleTasks: TaskType[] = [
    'text-generation',
    'basic-formatting', 
    'simple-qa'
  ];

  // Complex reasoning tasks: Use reasoning model for best results
  const reasoningTasks: TaskType[] = [
    'bias-detection',
    'complex-scoring', 
    'technical-analysis',
    'skills-assessment',
    'mathematical-analysis',
    'strategic-analysis'
  ];

  if (simpleTasks.includes(taskType)) {
    return BUDGET_MODEL;
  }
  
  if (reasoningTasks.includes(taskType)) {
    return REASONING_MODEL;
  }
  
  // Use budget model for standard tasks (faster & cheaper)
  return BUDGET_MODEL;
}

/**
 * Get model configuration with cost estimation
 */
export function getModelConfig(model: keyof typeof MODEL_CONFIGS) {
  return MODEL_CONFIGS[model];
}

/**
 * Estimate cost for a request (rough estimation)
 */
export function estimateRequestCost(
  model: keyof typeof MODEL_CONFIGS,
  inputTokens: number,
  outputTokens: number
): number {
  const config = MODEL_CONFIGS[model];
  const inputCost = (inputTokens / 1_000_000) * config.cost.input;
  const outputCost = (outputTokens / 1_000_000) * config.cost.output;
  return inputCost + outputCost;
}